import { useState, useEffect } from 'react';
import { apiRequest, uploadFiles, API_BASE } from '../../lib/api';

type ImageFile = {
  name: string;
  url: string;
  size: number;
  createdAt: string;
};

export default function ImageManager({ showToast }: { showToast: (msg: string, type?: 'info'|'error'|'warning') => void }) {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadImages = async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ files: ImageFile[] }>('/upload', undefined, true);
      setImages(res.files || []);
    } catch (e) {
      showToast('Lỗi tải danh sách ảnh', 'error');
    } finally {
      setLoading(false);
    }
  };

  const [confirmDeleteImage, setConfirmDeleteImage] = useState<string | null>(null);

  useEffect(() => {
    loadImages();
  }, []);

  const handleUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('type', 'file');
    input.setAttribute('multiple', 'true');
    input.click();

    input.onchange = async () => {
      const files = input.files;
      if (!files || files.length === 0) return;
      setUploading(true);
      showToast('Đang tải file lên...', 'info');
      try {
        const fileArray = Array.from(files);
        await uploadFiles(fileArray);
        showToast('Tải file thành công!', 'info');
        loadImages();
      } catch (e) {
        showToast('Tải file thất bại', 'error');
      } finally {
        setUploading(false);
      }
    };
  };

  const confirmDelete = async () => {
    if (!confirmDeleteImage) return;
    try {
      await apiRequest(`/upload/${confirmDeleteImage}`, { method: 'DELETE' }, true);
      showToast('Đã xóa file', 'info');
      loadImages();
    } catch (e) {
      showToast('Lỗi xóa file', 'error');
    } finally {
      setConfirmDeleteImage(null);
    }
  };

  const copyLink = (url: string) => {
    const fullUrl = API_BASE.replace('/api', '') + url;
    navigator.clipboard.writeText(fullUrl);
    showToast('Đã sao chép link file!', 'info');
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '24px', borderRadius: '16px', border: '1px solid var(--glass-border)', marginTop: '40px', backdropFilter: 'blur(16px)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', color: '#f8fafc', fontWeight: 700 }}>Quản lý file hệ thống (Hình ảnh & Firmware)</h2>
        <button onClick={handleUpload} disabled={uploading} style={{
          padding: '10px 18px', borderRadius: '10px',
          background: 'linear-gradient(135deg, #e11d48, #be123c)',
          color: 'white', border: 'none', cursor: uploading ? 'not-allowed' : 'pointer',
          fontWeight: 600, fontSize: '14px',
          boxShadow: '0 4px 14px rgba(225, 29, 72, 0.4)'
        }}>
          {uploading ? 'Đang tải...' : 'Tải file mới'}
        </button>
      </div>

      {loading ? <p style={{ color: '#94a3b8' }}>Đang tải danh sách file...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
          {images.map(img => {
            const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(img.name);
            return (
              <div key={img.name} style={{ border: '1px solid var(--glass-border)', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'rgba(0, 0, 0, 0.3)' }}>
                <div style={{ 
                  height: '120px', 
                  background: 'rgba(0, 0, 0, 0.4)', 
                  backgroundImage: isImage ? `url(${API_BASE.replace('/api', '') + img.url})` : 'none', 
                  backgroundSize: 'contain', 
                  backgroundPosition: 'center', 
                  backgroundRepeat: 'no-repeat',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  {!isImage && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#94a3b8' }}>
                      <span style={{ fontSize: '32px' }}>📄</span>
                      <span style={{ fontSize: '12px', marginTop: '8px', fontWeight: 600 }}>FILE</span>
                    </div>
                  )}
                </div>
              <div style={{ padding: '12px', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, color: '#f8fafc' }} title={img.name}>{img.name}</div>
                <div style={{ color: '#94a3b8', fontSize: '12px' }}>{formatSize(img.size)} • {new Date(img.createdAt).toLocaleDateString('vi-VN')}</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '8px' }}>
                  <button onClick={() => copyLink(img.url)} style={{
                    flex: 1, padding: '6px 10px', borderRadius: '6px',
                    border: '1px solid var(--glass-border)',
                    background: 'rgba(255, 255, 255, 0.06)',
                    color: '#f8fafc',
                    cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                    transition: 'all 0.15s ease'
                  }}>Copy Link</button>
                  <button onClick={() => setConfirmDeleteImage(img.name)} style={{
                    padding: '6px 12px', borderRadius: '6px',
                    border: '1px solid rgba(225, 29, 72, 0.3)',
                    background: 'rgba(225, 29, 72, 0.15)',
                    color: '#f43f5e',
                    cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                    transition: 'all 0.15s ease'
                  }}>Xóa</button>
                </div>
              </div>
            </div>
            );
          })}
          {images.length === 0 && <p style={{ color: '#94a3b8', gridColumn: '1 / -1' }}>Chưa có file nào được tải lên.</p>}
        </div>
      )}

      {confirmDeleteImage && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)'
        }} onClick={() => setConfirmDeleteImage(null)}>
          <div style={{
            background: '#0e121a', borderRadius: '16px', padding: '32px',
            border: '1px solid var(--glass-border)',
            width: '90%', maxWidth: '400px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
            color: '#f8fafc'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>Xác nhận xoá</h3>
              <button onClick={() => setConfirmDeleteImage(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.5, marginBottom: '32px' }}>
              Bạn có chắc chắn muốn xóa file <strong>{confirmDeleteImage}</strong> này khỏi hệ thống không? Hành động này không thể hoàn tác.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setConfirmDeleteImage(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)',
                color: '#cbd5e1', border: '1px solid var(--glass-border)', fontWeight: 600, cursor: 'pointer',
              }}>Hủy</button>
              <button type="button" onClick={confirmDelete} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: '#dc2626',
                color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)',
              }}>Xác nhận xoá</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
